import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

export interface ToolReview {
  id: string
  tool_id: string
  user_id: string
  user_email: string
  rating: number
  review_text: string
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
}

export function useToolReviews(toolId: string) {
  const { user } = useAuth()
  const [reviews, setReviews] = useState<ToolReview[]>([])
  const [userReview, setUserReview] = useState<ToolReview | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchReviews()
  }, [toolId, user])

  async function fetchReviews() {
    try {
      setLoading(true)

      // Get approved reviews
      const { data: approvedReviews, error: reviewsError } = await supabase
        .from('tool_reviews')
        .select('*')
        .eq('tool_id', toolId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false })

      if (reviewsError) throw reviewsError

      setReviews(approvedReviews || [])

      // Get user's review if logged in (any status)
      if (user) {
        const { data: userReviewData } = await supabase
          .from('tool_reviews')
          .select('*')
          .eq('tool_id', toolId)
          .eq('user_id', user.id)
          .single()

        setUserReview(userReviewData || null)
      }
    } catch (error) {
      console.error('Error fetching reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  async function submitReview(rating: number, reviewText: string) {
    if (!user) {
      throw new Error('Must be logged in to submit review')
    }

    try {
      const { error } = await supabase.from('tool_reviews').insert({
        tool_id: toolId,
        user_id: user.id,
        user_email: user.email || '',
        rating,
        review_text: reviewText,
        status: 'pending',
      })

      if (error) throw error

      // Refresh reviews
      await fetchReviews()
    } catch (error) {
      console.error('Error submitting review:', error)
      throw error
    }
  }

  return {
    reviews,
    userReview,
    loading,
    submitReview,
    refresh: fetchReviews,
  }
}
